// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xngGaPLPEvent from "@guestsupply/App/Handlers/GoogleAnalytics/PLP/PLPEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { XProductFilters } from "@guestsupply/XDL/types";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AttributeTypeFacetModel,
    CategoryFacetModel,
    FacetModel,
    PriceFacetModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Search from "@insite/mobius/Icons/Search";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}
// CNG-CHANGES
const mapStateToProps = (state: CustomAppState) => {
    const {
        pages: {
            productList: { productFilters, unfilteredApiParameter, filteredApiParameter },
        },
        context: {
            session: { currency, language },
        },
    } = state;
    if (getProductListDataView(state).value) {
        const unfilteredDataView = getProductsDataView(state, unfilteredApiParameter);
        const unfilteredProductCollection = unfilteredDataView.value ? unfilteredDataView : undefined;

        const filteredDataView = getProductsDataView(state, filteredApiParameter);
        const filteredProductCollection = filteredDataView.value ? filteredDataView : undefined;
        const mergeAttributeTypeFacets = (
            unfilteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
            filteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
        ) => {
            if (!unfilteredAttributeTypeFacets) {
                return filteredAttributeTypeFacets;
            }

            if (!filteredAttributeTypeFacets) {
                return unfilteredAttributeTypeFacets;
            }

            return unfilteredAttributeTypeFacets.concat(
                filteredAttributeTypeFacets.filter(
                    o => !unfilteredAttributeTypeFacets.some(p => p.attributeTypeId === o.attributeTypeId),
                ),
            );
        };

        return {
            loaded: true,
            productFilters: state.pages.productList.productFilters,
            hideSearchWithin: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
            stockedItemsOnly: productFilters.stockedItemsOnly ?? false,
            previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts ?? false,
            brandFacets: unfilteredProductCollection?.brandFacets || filteredProductCollection?.brandFacets,
            productLineFacets:
                unfilteredProductCollection?.productLineFacets || filteredProductCollection?.productLineFacets,
            categoryFacets: unfilteredProductCollection?.categoryFacets,
            priceFacets:
                unfilteredProductCollection?.priceRange?.priceFacets ||
                filteredProductCollection?.priceRange?.priceFacets,
            attributeTypeFacets: mergeAttributeTypeFacets(
                unfilteredProductCollection?.attributeTypeFacets,
                filteredProductCollection?.attributeTypeFacets,
            ),
            searchWithinQueries: productFilters.searchWithinQueries,
            selectedBrandIds: productFilters.brandIds,
            selectedProductLineIds: productFilters.productLineIds,
            selectedPriceFilters: productFilters.priceFilters,
            selectedAttributeValuesIds: productFilters.attributeValueIds,
            selectedCategoryId: productFilters.categoryId,
            currency,
            language,
            brandStandards: (state.pages.productList.productFilters as XProductFilters).brandStandards as any,
            isAvendraPricing: (state.pages.productList.productFilters as XProductFilters)?.isAvendraPricing as any,
            // xng-change: add google event
            xGoogleAnalytics: state.xGoogleAnalytics,
        };
    }
    return {
        loaded: false,
        productFilters: state.pages.productList.productFilters,
        hideSearchWithin: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
        stockedItemsOnly: false,
        previouslyPurchasedProducts: false,
        brandFacets: [] as FacetModel[],
        productLineFacets: [] as FacetModel[],
        categoryFacets: [] as CategoryFacetModel[],
        priceFacets: [] as PriceFacetModel[],
        attributeTypeFacets: [] as AttributeTypeFacetModel[],
        searchWithinQueries: [] as string[],
        selectedBrandIds: [] as string[],
        selectedProductLineIds: [] as string[],
        selectedPriceFilters: [] as string[],
        selectedAttributeValuesIds: [] as string[],
        selectedCategoryId: "",
        currency: null,
        language: null,
        brandStandards: [] as FacetModel[],
        isAvendraPricing: false,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    // XNG-CJHANGES
    clearAllProductFilters,
    // xng-change: add google event
    xngGaPLPEvent,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListSearchWithinStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
    // XNG-CHANGES
    refineWrapper?: InjectableCss;
    clearAllLink?: LinkPresentationProps;
    filterText?: TypographyProps;
}

export const searchWithinStyles: ProductListSearchWithinStyles = {};

const styles = searchWithinStyles;

const ProductListSearchWithin: FC<Props> = ({
    loaded,
    addProductFilters,
    productFilters,
    hideSearchWithin,
    // XNG-CHANGES
    stockedItemsOnly,
    previouslyPurchasedProducts,
    brandFacets,
    productLineFacets,
    priceFacets,
    attributeTypeFacets,
    searchWithinQueries,
    selectedBrandIds,
    selectedProductLineIds,
    selectedPriceFilters,
    selectedAttributeValuesIds,
    selectedCategoryId,
    clearAllProductFilters,
    isAvendraPricing,
    brandStandards,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaPLPEvent,
}) => {
    if (!loaded) {
        return null;
    }

    const [searchText, setSearchText] = useState("");

    const doSearch = () => {
        if (searchText.trim() !== "") {
            addProductFilters({ searchWithinQueries: [searchText] });
            // xng-change: add google event
            xngGaPLPEvent({
                plpEventType: GOOGLE_EVENTS.PLP_SEARCH,
                includePLPEvent: xGoogleAnalytics.includePLPSearchEvent,
                searchQuery: searchText,
            });
        }
        setSearchText("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    let pageType: string;
    if (productFilters.pageProductLineId) {
        pageType = "Product Line";
    } else if (productFilters.pageBrandId) {
        pageType = "Brand";
    } else if (productFilters.pageCategoryId) {
        pageType = "Category";
    } else {
        pageType = "Results";
    }

    const label = translate(`Search Within ${pageType}`);

    if (hideSearchWithin) {
        return null;
    }

    // XNG-CHANGES : GUEST-2656 : Shwing umique results and removing empty spaces from the results
    const hasSearchWithinQueries = searchWithinQueries?.length && searchWithinQueries.find(query => query.length > 0);

    // XNG-CHANGSE
    const anyActiveFilters =
        hasSearchWithinQueries ||
        stockedItemsOnly ||
        previouslyPurchasedProducts ||
        (brandFacets && selectedBrandIds?.length) ||
        (productLineFacets && selectedProductLineIds?.length) ||
        (priceFacets && selectedPriceFilters?.length) ||
        (attributeTypeFacets && selectedAttributeValuesIds?.length) ||
        selectedCategoryId ||
        isAvendraPricing || // XNG-CHANGES
        (brandStandards && brandStandards.length > 0); // XNG-CHANGES

    return (
        <>
            {/* XNG-CHANGES : Adding refine by  */}
            <StyledWrapper {...styles.refineWrapper}>
                <Typography {...styles.filterText} data-test-selector="ProductListRefineByText">
                    {translate("Refine by")}
                </Typography>
                {anyActiveFilters && (
                    <Link
                        {...styles.clearAllLink}
                        onClick={() => clearAllProductFilters()}
                        data-test-selector="productListClearAll"
                        aria-label="Clear All Filters"
                    >
                        {translate("Clear All Filters")}
                    </Link>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <Typography forwardAs="label" htmlFor="productsKeywordSearch" {...styles.titleText}>
                    {label}
                </Typography>
                <TextField
                    {...styles.searchTextField}
                    iconClickableProps={{ onClick: doSearch, "aria-label": `Search within ${pageType}` }}
                    value={searchText}
                    onChange={event => setSearchText(event.currentTarget.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={translate("Enter keywords")}
                    type="text"
                    data-test-selector="productListSearchWithin"
                    id="productsKeywordSearch"
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListSearchWithin),
    definition: {
        group: "Product List",
        displayName: "Search Within",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
